import React, { useCallback, useEffect, useRef, useState } from "react";
import { getSubjects } from "../../../services/SubjectService";
import { Link } from "react-router-dom";
import {
  getImportableEntities,
  downloadTemplate,
  exportEntityData,
  importEntityFile,
} from "../../../services/ImportExportService";
import { useTranslation } from "react-i18next";

import i18n from "../../../i18n";
const statusBadge = {
  created: { get label() { return i18n.t("pages.importExport.cree"); }, className: "badge bg-success" },
  updated: { get label() { return i18n.t("pages.importExport.misJour"); }, className: "badge bg-info" },
  skipped: { get label() { return i18n.t("pages.importExport.ignore"); }, className: "badge bg-warning" },
  error: { get label() { return i18n.t("common.ui.erreur"); }, className: "badge bg-danger" },
};

const ImportExport = () => {
  const { t } = useTranslation();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [selectedKey, setSelectedKey] = useState("");
  const [mode, setMode] = useState("skip");
  const [file, setFile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const fileInputRef = useRef(null);

  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, message }
  const [importResult, setImportResult] = useState(null); // { summary, results }

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getImportableEntities();
      const list = res.data?.data || [];
      setEntities(list);
      if (list.length && !selectedKey) setSelectedKey(list[0].key);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          t("pages.importExport.impossibleChargerListeEntites")
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const selectedEntity = entities.find((e) => e.key === selectedKey) || null;
  // Questions : Word et PDF acceptés en plus d'Excel ; la matière se choisit alors ici.
  const isQuestions = selectedKey === "questions";
  const isDocumentFile = /\.(docx|pdf)$/i.test(file?.name || "");

  useEffect(() => {
    if (!isQuestions || subjects.length) return;
    getSubjects({ page: 1, limit: 200 })
      .then((res) => setSubjects(res.data?.data || []))
      .catch(() => {});
  }, [isQuestions, subjects.length]);

  const handleSelectEntity = (key) => {
    setSelectedKey(key);
    setImportResult(null);
    setFeedback(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = async () => {
    if (!selectedEntity) return;
    setDownloading(true);
    setFeedback(null);
    try {
      await downloadTemplate(selectedEntity.key, selectedEntity.key);
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message ||
          t("pages.importExport.impossibleTelechargerModele"),
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleExportData = async () => {
    if (!selectedEntity) return;
    setExporting(true);
    setFeedback(null);
    try {
      await exportEntityData(selectedEntity.key, selectedEntity.key);
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message ||
          t("pages.importExport.impossibleExporterDonnees"),
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!selectedEntity || !file) return;
    if (isQuestions && isDocumentFile && !subjectId) {
      setFeedback({ type: "danger", message: t("pages.importExport.questionsSubjectRequired") });
      return;
    }

    setImporting(true);
    setFeedback(null);
    setImportResult(null);
    try {
      const res = await importEntityFile(selectedEntity.key, file, mode, isQuestions && isDocumentFile ? subjectId : "");
      const { summary, results } = res.data?.data || {};
      setImportResult({ summary, results: results || [] });
      setFeedback({
        type: summary?.errors ? "warning" : "success",
        message: t("pages.importExport.importTermineCreeMisJourIgnore", { v0: summary?.created || 0, v1: summary?.updated || 0, v2: summary?.skipped || 0, v3: summary?.errors || 0 }),
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message || t("pages.importExport.echecImportFichier"),
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("pages.importExport.importExport")}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {feedback && (
          <div
            className={`alert alert-${feedback.type} alert-dismissible fade show`}
            role="alert"
          >
            {feedback.message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setFeedback(null)}
            ></button>
          </div>
        )}

        {loadError && <div className="alert alert-danger">{loadError}</div>}

        {!loading && !loadError && entities.length === 0 && (
          <div className="alert alert-info">
            {t("pages.importExport.aucuneEntiteImportExportNEst")}
          </div>
        )}

        {entities.length > 0 && (
          <div className="row">
            {/* Sélection de l'entité */}
            <div className="col-lg-4 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="mb-0">{t("common.ui.entite")}</h4>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    {t("pages.importExport.choisissezTableModeleImporterPourLaquelle")}
                  </p>
                  <div className="list-group">
                    {entities.map((entity) => (
                      <button
                        key={entity.key}
                        type="button"
                        className={`list-group-item list-group-item-action ${
                          entity.key === selectedKey ? "active" : ""
                        }`}
                        onClick={() => handleSelectEntity(entity.key)}
                      >
                        {entity.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Détail entité + actions */}
            <div className="col-lg-8 mb-3">
              {selectedEntity && (
                <>
                  <div className="card mb-3">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
                      <h4 className="mb-0">{selectedEntity.label}</h4>
                      {selectedEntity.canExport && (
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center"
                            onClick={handleExportData}
                            disabled={exporting}
                          >
                            <i className="ti ti-file-export me-2"></i>
                            {exporting ? t("pages.importExport.export") : t("pages.importExport.exporterDonneesExistantes")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary d-flex align-items-center"
                            onClick={handleDownloadTemplate}
                            disabled={downloading}
                          >
                            <i className="ti ti-download me-2"></i>
                            {downloading
                              ? t("pages.importExport.generation")
                              : t("pages.importExport.telechargerModeleVierge")}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="card-body">
                      <p className="text-muted mb-2">
                        {t("pages.importExport.champsAttendusDansFichier")}
                      </p>
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr>
                              <th>{t("pages.importExport.colonne")}</th>
                              <th>{t("pages.importExport.libelle")}</th>
                              <th>{t("common.fields.type")}</th>
                              <th>{t("pages.importExport.obligatoire")}</th>
                              <th>{t("pages.importExport.reference")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedEntity.fields.map((f) => (
                              <tr key={f.key}>
                                <td><code>{f.key}</code></td>
                                <td>{f.label}</td>
                                <td>{f.type}</td>
                                <td>{f.required ? t("common.fields.yes") : t("common.fields.no")}</td>
                                <td>
                                  {f.reference ? (
                                    <span className="text-muted">
                                      {t("pages.importExport.code", { reference: f.reference })}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {selectedEntity.canImport && (
                    <div className="card">
                      <div className="card-header">
                        <h4 className="mb-0">{t("pages.importExport.importerFichier")}</h4>
                      </div>
                      <div className="card-body">
                        <form onSubmit={handleImport}>
                          <div className="mb-3">
                            <label className="form-label">
                              {t("pages.importExport.fichierXlsxXlsCsv")}
                            </label>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="form-control"
                              accept={isQuestions ? ".xlsx,.xls,.csv,.docx,.pdf" : ".xlsx,.xls,.csv"}
                              onChange={(e) =>
                                setFile(e.target.files?.[0] || null)
                              }
                            />
                            {isQuestions && (
                              <small className="text-muted d-block mt-1">{t("pages.importExport.questionsDocumentHelp")}</small>
                            )}
                          </div>

                          {isQuestions && isDocumentFile && (
                            <div className="mb-3">
                              <label className="form-label">
                                {t("pages.importExport.questionsSubject")} <span className="text-danger">*</span>
                              </label>
                              <select className="form-select" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
                                <option value="">{t("common.crud.selectPlaceholder")}</option>
                                {subjects.map((sub) => (
                                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="form-label d-block">
                              {t("pages.importExport.siLigneCorrespondEnregistrementDejaExistant")}
                            </label>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="mode"
                                id="mode-skip"
                                checked={mode === "skip"}
                                onChange={() => setMode("skip")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="mode-skip"
                              >
                                {t("pages.importExport.lIgnorer")}
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="mode"
                                id="mode-update"
                                checked={mode === "update"}
                                onChange={() => setMode("update")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="mode-update"
                              >
                                {t("pages.importExport.laMettreJour")}
                              </label>
                            </div>
                          </div>

                          <button
                            type="submit"
                            className="btn btn-primary d-flex align-items-center"
                            disabled={!file || importing}
                          >
                            <i className="ti ti-upload me-2"></i>
                            {importing ? t("pages.importExport.importCours") : t("pages.importExport.importer")}
                          </button>
                        </form>
                      </div>
                    </div>
                  )}

                  {importResult && importResult.results.length > 0 && (
                    <div className="card mt-3">
                      <div className="card-header">
                        <h4 className="mb-0">{t("pages.importExport.detailImport")}</h4>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                          <table className="table table-sm table-bordered mb-0">
                            <thead>
                              <tr>
                                <th>{t("pages.importExport.ligne")}</th>
                                <th>{t("common.crud.statusColumn")}</th>
                                <th>{t("pages.importExport.detail")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importResult.results.map((r) => (
                                <tr key={r.row}>
                                  <td>{r.row}</td>
                                  <td>
                                    <span
                                      className={
                                        statusBadge[r.status]?.className ||
                                        "badge bg-secondary"
                                      }
                                    >
                                      {statusBadge[r.status]?.label ||
                                        r.status}
                                    </span>
                                  </td>
                                  <td>{(r.messages || []).join(" ")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportExport;